import { getRestorationZoneDemandForecastData } from "api/dataVisualisation/restorationZone/restorationZoneDemandForecast";
import FullWidthSection from "components/components/FullWidthSection/FullWidthSection";
import { StyledDiv } from "components/components/SectionWithErrorHandling/style";
import SubmissionDateTimeFilter from "components/components/SubmissionDateTimeFilter/SubmissionDateTimeFilter";
import TableWithErrorHandling from "components/components/TableWithErrorHandling/TableWithErrorHandling";
import DataExporter from "components/dataVisualization/dataExporterComponents/DataExporter/DataExporter";
import RestorationZoneDemandForecastTable from "components/dataVisualization/restorationZone/DemandForecast/RestorationZoneDemandForecastTable/RestorationZoneDemandForecastTable";
import useRestorationZoneDemandForecastData from "components/dataVisualization/restorationZone/DemandForecast/useRestorationZoneDemandForecastData";
import {
  Region,
  RegionFilter,
} from "components/dataVisualization/restorationZone/RegionFilter";
import { TableFooter } from "components/dataVisualization/tableComponents/style";
import { useDataExport } from "contexts/DataExportContext";
import { DataDownloadFormat } from "models/dataDownload/dataDownloadFormat";
import DateFilterModel from "models/filterModels/dateFilterModel";
import {
  RestorationZoneDemandForecastModel,
  RestorationZoneDemandForecastTableModel,
} from "models/restorationZone/restorationZoneDemandForecast";
import React, { useEffect, useMemo, useState } from "react";
import { downloadDataToFile } from "utils/DataDownloads/FetchDataForFileExport/DownloadDataToFile";
import { addDaysToDate, floorToHalfHour } from "utils/dateHelpers";

export const mapToRestorationZoneDemandForecastRow = (
  data: RestorationZoneDemandForecastModel[]
): RestorationZoneDemandForecastTableModel[] =>
  data.map((item, key) => ({
    id: key,
    submissionDateTime: new Date(item.submissionDateTime),
    settlementDate: new Date(item.settlementDate),
    region: item.region,
    gspGroupId: item.gspGroupId,
    forecastDemand60: item.forecastDemand60,
    forecastDemand100: item.forecastDemand100,
  }));

const RestorationZoneDemandForecastContainer: React.FC = () => {
  const [regionFilter, setRegionFilter] = useState<Region>(Region.AllRegions);

  const defaultEndTime = floorToHalfHour(new Date());
  const defaultDateFilter = useMemo(
    () =>
      new DateFilterModel(addDaysToDate(defaultEndTime, -7), defaultEndTime),
    [defaultEndTime]
  );

  const [dateFilter, setDateFilter] =
    useState<DateFilterModel>(defaultDateFilter);

  const {
    data: rawData,
    fetchData,
    status,
  } = useRestorationZoneDemandForecastData(dateFilter, regionFilter);

  useEffect(fetchData, [fetchData]);

  const tableData = useMemo(
    () => (rawData ? mapToRestorationZoneDemandForecastRow(rawData) : []),
    [rawData]
  );

  const { dispatch: dataExportCountDispatch } = useDataExport();

  const downloadFile = (fileFormat: DataDownloadFormat): void => {
    downloadDataToFile(
      () =>
        getRestorationZoneDemandForecastData(
          dateFilter,
          regionFilter,
          fileFormat
        ),
      `RestorationZoneDemandForecast-${dateFilter.startDate.toISOString()}-${dateFilter.endDate.toISOString()}`,
      fileFormat,
      dataExportCountDispatch
    );
  };

  return (
    <>
      <FullWidthSection className="is-grey-section">
        <StyledDiv className="container px-6">
          <SubmissionDateTimeFilter
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
          />
        </StyledDiv>
      </FullWidthSection>
      <br />
      <RegionFilter
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
      />
      <TableWithErrorHandling
        data={tableData}
        dataFetchStatus={status}
        rerequestData={fetchData}
      >
        <RestorationZoneDemandForecastTable data={tableData} />
      </TableWithErrorHandling>
      <TableFooter rightAligned>
        <DataExporter downloadData={downloadFile} />
      </TableFooter>
    </>
  );
};

export default RestorationZoneDemandForecastContainer;
